import { isAbsAdminUser, type NotasUser } from "@/modules/Clientes/clienteEstadoCuentaNotas.permissions";
import { sessionIsElevated } from "@/lib/modulePermissions";

/** Mismo texto en GUI (Dialog/alert) y API 400 `pv_no_asignado`. */
export const PV_NO_ASIGNADO_MSG =
  "Necesitás estar asignado a un punto de venta para continuar.";

export const PV_NO_ASIGNADO_CODE = "pv_no_asignado";

/** Código interno: Predeterminado = el PV que se crea en este Guardar (código aún auto). */
export const NUEVO_PV_DEFAULT_SENTINEL = "__nuevo__";

export type PvUser = NotasUser & {
  puntosVenta?: unknown[] | null;
  puntoVentaDefault?: string | number | null;
  punto_venta_default?: string | number | null;
};

export type PvOperation = "venta" | "presupuesto" | "remito";

export function hasAssignedPv(user: PvUser | null | undefined): boolean {
  return Array.isArray(user?.puntosVenta) && user.puntosVenta.length > 0;
}

/**
 * Vendedor (no admin/staff) sin PV asignado: bloquear factura, presupuesto y
 * remito de ventas. Admin/staff sigue pudiendo operar (primer PV del catálogo
 * en venta/presupuesto; remito admin = PV de la factura, F5). El nombre del
 * rol no saltea este corte.
 */
export function vendedorBlockedWithoutPv(
  user: PvUser | null | undefined,
): boolean {
  if (isAbsAdminUser(user) || sessionIsElevated(user)) return false;
  return !hasAssignedPv(user);
}

/** Las 3 ops aprobadas comparten el mismo corte. */
export function canConfirmPvOperation(
  user: PvUser | null | undefined,
  _op: PvOperation,
): boolean {
  return !vendedorBlockedWithoutPv(user);
}

export function pvBlockMessage(
  user: PvUser | null | undefined,
  op: PvOperation,
): string | null {
  if (canConfirmPvOperation(user, op)) return null;
  return PV_NO_ASIGNADO_MSG;
}

type PvRaw = {
  id?: unknown;
  codigo?: unknown;
  codigoPV?: unknown;
  nombre?: unknown;
  sucursal?: unknown;
  sucursal_nombre?: unknown;
  sucursalNombre?: unknown;
  activo?: unknown;
};

export type AssignedPuntoVenta = {
  id: number;
  codigo: string;
  nombre: string;
  sucursal: number;
  sucursal_nombre?: string;
  activo: boolean;
};

/** PV del login (`user.puntosVenta`); no listar el catálogo para vendedor. */
export function assignedPuntosVentaFromUser(
  user: PvUser | null | undefined,
): AssignedPuntoVenta[] {
  const raw = user?.puntosVenta;
  if (!Array.isArray(raw)) return [];
  const out: AssignedPuntoVenta[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const pv = item as PvRaw;
    const id = Number(pv.id);
    if (!Number.isFinite(id) || id <= 0) continue;
    const sucursalRaw = pv.sucursal;
    let sucursal = 0;
    if (typeof sucursalRaw === "number") sucursal = sucursalRaw;
    else if (sucursalRaw && typeof sucursalRaw === "object" && "id" in sucursalRaw) {
      sucursal = Number((sucursalRaw as { id?: unknown }).id) || 0;
    }
    out.push({
      id,
      codigo: String(pv.codigoPV || pv.codigo || ""),
      nombre: String(pv.nombre || ""),
      sucursal,
      sucursal_nombre: String(
        pv.sucursalNombre ||
          pv.sucursal_nombre ||
          (typeof sucursalRaw === "string" ? sucursalRaw : "") ||
          "",
      ),
      activo: pv.activo !== false,
    });
  }
  out.sort((a, b) => a.codigo.localeCompare(b.codigo));
  return out;
}

function defaultCodigoOf(user: PvUser | null | undefined): string {
  const raw = user?.puntoVentaDefault ?? user?.punto_venta_default;
  if (raw == null || raw === "") return "";
  return String(raw);
}

/** Predeterminado si está en la lista activa; si no, el primero por código. */
export function pickDefaultPuntoVenta(
  user: PvUser | null | undefined,
  assigned: AssignedPuntoVenta[] = assignedPuntosVentaFromUser(user),
): AssignedPuntoVenta | null {
  const active = assigned.filter((pv) => pv.activo);
  if (active.length === 0) return null;
  const code = defaultCodigoOf(user);
  if (code) {
    const byCode = active.find((pv) => pv.codigo === code);
    if (byCode) return byCode;
    const byId = active.find((pv) => String(pv.id) === code);
    if (byId) return byId;
  }
  return active[0];
}

export function nextDefaultCodigo(
  codes: string[],
  previous?: string | null,
): string | null {
  const unique = [...new Set(codes.filter(Boolean))];
  if (unique.length === 0) return null;
  if (previous && unique.includes(previous)) return previous;
  return [...unique].sort((a, b) => a.localeCompare(b))[0];
}

/**
 * Venta/presupuesto: asignados + Predeterminado.
 * Elevated (superuser / Super Usuario): catálogo completo; default = Predeterminado si está.
 */
export function resolveSalePuntosVenta(
  user: PvUser | null | undefined,
  catalog: AssignedPuntoVenta[] = [],
): { list: AssignedPuntoVenta[]; selected: AssignedPuntoVenta | null } {
  const assigned = assignedPuntosVentaFromUser(user).filter((pv) => pv.activo);
  if (sessionIsElevated(user) && catalog.length > 0) {
    const active = catalog.filter((pv) => pv.activo !== false);
    const list = active.length > 0 ? active : catalog;
    const preferred = pickDefaultPuntoVenta(user, assigned.length > 0 ? assigned : list);
    const selected =
      (preferred &&
        list.find((pv) => pv.id === preferred.id || pv.codigo === preferred.codigo)) ||
      list[0] ||
      null;
    return { list, selected };
  }
  if (assigned.length > 0) {
    return { list: assigned, selected: pickDefaultPuntoVenta(user, assigned) };
  }
  if (isAbsAdminUser(user) && catalog.length > 0) {
    const active = catalog.filter((pv) => pv.activo !== false);
    const list = active.length > 0 ? active : catalog;
    return { list, selected: list[0] ?? null };
  }
  return { list: [], selected: null };
}

export function isPvNoAsignadoApiError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as {
    error?: unknown;
    code?: unknown;
    message?: unknown;
    data?: { error?: unknown; message?: unknown };
    details?: { error?: unknown; message?: unknown } | string;
  };
  const pieces = [
    e.error,
    e.code,
    e.message,
    e.data?.error,
    e.data?.message,
    typeof e.details === "string" ? e.details : e.details?.error,
    typeof e.details === "object" ? e.details?.message : "",
  ]
    .filter((x) => typeof x === "string")
    .join(" ")
    .toLowerCase();
  return (
    pieces.includes(PV_NO_ASIGNADO_CODE) ||
    pieces.includes("punto de venta para continuar")
  );
}
